import { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown, Popconfirm } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { dateToString, getFullName } from '@/utils';
import { useGetChildSiblingGroupQuery } from '@/services/child-api';
import { useDeleteGroupMemberMutation, useGetGroupMembersQuery } from '@/services/sibling-group-api';
import { CaretDownSvg, EditSvg, PlusSvg, SearchSvg, UserPlusSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import Loading from '@/components/loading';
import { useState } from 'react';
import AddModal from '@/pages/main/sibling-group/view/add-modal';

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 16px;

    a {
      display: flex;
    }
  }

  p {
    margin: 0;
  }
`;

const AddSibglingButton = styled(Button)`
  background-color: #28a745;
  border-color: #28a745;
  color: white;
  width: 300px;
  height: 45px;
  border-radius: 8px;
  margin-top: 25px;
  margin-bottom: 25px;
  font-size: 1.2rem;

  &:hover,
  &:focus {
    background-color: #218838;
    border-color: #1e7e34;
    color: white !important;
  }
`;

const SiblingGroup = ({ childId, permissions }) => {
  const { data: groupData, isLoading: loadingGroupData } = useGetChildSiblingGroupQuery({
    childId,
  });

  const [isOpenAddModal, openAddModal] = useState(false);

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const pageNumber = queryParams.get('page') || 1;
  const {
    data: members,
    isFetching: loadingMembers,
    refetch,
  } = useGetGroupMembersQuery(
    {
      groupId: groupData?.siblingGroupId,
      pageNumber,
      excludeChildId: childId,
    },
    { skip: !groupData?.siblingGroupId },
  );

  const [deleteMember, { isLoading: deleting }] = useDeleteGroupMemberMutation();

  const columns = [
    {
      title: 'Child ID',
      dataIndex: 'childCode',
      render: (value, record) => <Link to={`/child/${record.childId}`}>{value}</Link>,
    },
    {
      title: 'Child Name',
      dataIndex: 'name',
      render: (value) => getFullName(value),
    },
    {
      title: 'DOB',
      dataIndex: 'birthInfo',
      render: (value) => (
        <div>
          <div>{dateToString(value.date)}</div>
          <i>({value.age} years)</i>
        </div>
      ),
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      render: (value) => value.name,
    },
    {
      title: 'Race',
      dataIndex: 'race',
      render: (value) => value.name,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value) => value.name,
    },
    permissions.adminStaffAndSuper && {
      title: (
        // <Button
        //   type="primary"
        //   icon={<PlusSvg fontSize="12" />}
        //   to={`/sibling-group/${groupData?.siblingGroupId}/child/add`}
        // >
        //   Add
        // </Button>
        <Dropdown
          menu={{
            items: [
              {
                key: 'add',
                icon: <UserPlusSvg />,
                label: <Link to={`/sibling-group/${groupData?.siblingGroupId}/add-child`}>Add</Link>,
              },
              {
                key: 'search',
                icon: <SearchSvg />,
                label: 'Select Existing',
                onClick: () => openAddModal(true),
              },
            ].filter((item) => item),
          }}
        >
          <Button type="link" className="user-button" style={{ gap: 6, color: 'inherit' }}>
            <PlusSvg />
            Add
            <CaretDownSvg />
          </Button>
        </Dropdown>
      ),
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure to delete this?"
          icon={<QuestionCircleOutlined style={{ color: '#ff4d4f' }} />}
          okText="Yes"
          cancelText="No"
          onConfirm={async () => {
            try {
              await deleteMember({
                groupId: groupData?.siblingGroupId,
                memberId: record.childId,
              }).unwrap();
              refetch();
            } catch (err) {}
          }}
        >
          <Button type="link" danger disabled={deleting}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ].filter((item) => item);

  const addExisting = () => {
    openAddModal(true);
  };

  return loadingGroupData ? (
    <Loading />
  ) : groupData ? (
    <>
      <Header>
        <label>
          Sibling Group Bio
          {permissions.adminStaffAndSuper && (
            <Button type="link" icon={<EditSvg />} to={`/sibling-group/${groupData?.siblingGroupId}/edit`} />
          )}
        </label>
        <p>{groupData?.bio}</p>
      </Header>
      <Table
        columns={columns}
        dataSource={members?.data}
        rowKey="childId"
        loading={loadingMembers}
        pageInfo={members}
        size="small"
      />
      {permissions.adminStaffAndSuper && (
      <AddModal groupId={childId} open={isOpenAddModal} refetch={refetch} onClose={() => openAddModal(false)} />
      )}
    </>
  ) : (
    <>
      <div className="col-4 col-xl-3 mx-auto" style={{ textAlign: 'center' }}>
        <div className="text-center pt-5 pb-5">
          <AddSibglingButton className="btn btn-success" onClick={addExisting}>
            Add To Sibling Group
          </AddSibglingButton>
        </div>
      </div>
      <AddModal groupId={childId} open={isOpenAddModal} refetch={refetch} onClose={() => openAddModal(false)} />;
    </>
  );
};

export default memo(SiblingGroup);
